/**
 * `sales_person.create_supplier` — inline supplier creation (point 28).
 *
 * Live-probed 2026-09-03; the probe body and its two responses are quoted in full in
 * `createSupplier.test.ts`, which pins every claim made here.
 *
 * ---- The response's field names lie, and it matters ----
 *
 * It answers `{status, supplier_name, platform_id}`. The field called `supplier_name` is NOT the
 * supplier's name — it is the DOCNAME, with spaces replaced by underscores (`Fujairah_Marine`).
 * The doc's real `supplier_name` keeps its spaces and is never returned at all.
 *
 * So both halves have to come from different places: the Link value from the response, the display
 * label from what the user typed. Getting this backwards is CLAUDE.md's most-repeated bug in its
 * purest form — a name that looks like an id, one character away from matching.
 *
 * ---- The email is accepted and discarded ----
 *
 * The endpoint takes an `email` parameter, returns success, and stores it nowhere: the created
 * document has no email field set (probed by reading it straight back). It is still SENT, so the
 * day the backend starts honouring it nothing here needs to change — but `email` comes back `''`,
 * and `NewSupplierBar` tells the purchaser to add the address in the supplier master. Filed as
 * `E6` in `docs/bosun-frappe-asks.md`.
 */

import { callPost } from './bosun'
import type { Supplier } from '../types'

export interface WireCreateSupplierResult {
  status?: string
  /** MISLABELLED — this is the docname (underscored), not the display name. See the header. */
  supplier_name?: string
  platform_id?: string
}

export function toCreatedSupplier(wire: WireCreateSupplierResult, typedName: string): Supplier {
  if (wire?.status !== 'success') {
    throw new Error(`Could not create the supplier${wire?.status ? ` (${wire.status})` : ''}.`)
  }
  // `supplier_name` first, `platform_id` only as a fallback: on the probe they were identical, and
  // if they ever diverge the field the server derives the docname from is the one to trust.
  const code = wire.supplier_name || wire.platform_id || ''
  if (code === '') throw new Error('create_supplier returned no docname to link the supplier by')

  return {
    code,
    // What the user typed IS what the document stores — the response simply never sends it back.
    name: typedName,
    // Honest, not a guess: the endpoint drops it. See the header.
    email: '',
  }
}

export async function createSupplier(name: string, email: string): Promise<Supplier> {
  const wire = await callPost<WireCreateSupplierResult>('sales_person', 'create_supplier', {
    supplier_name: name,
    // Sent even though it is currently discarded, so this side is already correct when `E6` lands.
    email,
  })
  return toCreatedSupplier(wire, name)
}
